import { apiClient, cartApi } from '@/services/api';
import { store } from '@/store';
import { fetchCart } from '@/store/slices/cartSlice';
import toast from 'react-hot-toast';

interface PendingAction {
  method: string;
  url: string;
  data?: any;
  params?: any;
  headers?: any;
  timestamp: number;
}

/**
 * Execute a pending action that was stored before redirecting to login
 */
export const executePendingAction = async (): Promise<boolean> => {
  try {
    const pendingActionStr = sessionStorage.getItem('pendingAction');
    if (!pendingActionStr) {
      console.log('No pending action found in sessionStorage');
      return false;
    }

    const pendingAction: PendingAction = JSON.parse(pendingActionStr);
    console.log('Found pending action:', pendingAction);
    
    // Check if action is too old (more than 10 minutes)
    const actionAge = Date.now() - pendingAction.timestamp;
    if (actionAge > 10 * 60 * 1000) {
      console.log('Pending action expired, removing');
      sessionStorage.removeItem('pendingAction');
      return false;
    }

    // Remove the pending action from storage (we'll execute it now)
    sessionStorage.removeItem('pendingAction');
    console.log('Removed pending action from storage, executing now...');

    // Execute the pending action
    let response;
    // Normalize the URL - remove base URL if present, ensure it starts with /
    let url = pendingAction.url;
    
    // Remove base URL if it's included (e.g., http://localhost:8000/api/v1/cart/add-item -> /cart/add-item)
    const baseUrlPattern = /^https?:\/\/[^\/]+(\/api\/v1)?/;
    if (baseUrlPattern.test(url)) {
      url = url.replace(baseUrlPattern, '');
    }
    
    // Ensure URL starts with /
    if (!url.startsWith('/')) {
      url = `/${url}`;
    }
    
    // Remove /api/v1 prefix if present (it will be added by apiClient)
    if (url.startsWith('/api/v1/')) {
      url = url.replace('/api/v1', '');
    }

    console.log('Executing pending action:', {
      method: pendingAction.method,
      originalUrl: pendingAction.url,
      normalizedUrl: url,
      data: pendingAction.data,
      params: pendingAction.params,
    });

    try {
      // Special handling for cart actions - use cartApi directly for better reliability
      const isCartAddItem = url.includes('/cart/add-item') || 
                           url === '/cart/add-item' || 
                           url.includes('cart/add-item') ||
                           (url.includes('cart') && pendingAction.method === 'POST');
      
      if (isCartAddItem) {
        console.log('Detected cart add-item action, using cartApi.addItem');
        console.log('Pending action data:', pendingAction.data);
        console.log('Data type:', typeof pendingAction.data);
        
        // Handle different data formats
        let productId: number | undefined;
        let quantity: number = 1;
        
        if (pendingAction.data) {
          // If data is already an object
          if (typeof pendingAction.data === 'object' && !Array.isArray(pendingAction.data)) {
            productId = pendingAction.data.product_id || pendingAction.data.productId;
            quantity = pendingAction.data.quantity || 1;
          } 
          // If data is a string, try to parse it
          else if (typeof pendingAction.data === 'string') {
            try {
              const parsed = JSON.parse(pendingAction.data);
              productId = parsed.product_id || parsed.productId;
              quantity = parsed.quantity || 1;
            } catch (e) {
              console.error('Could not parse cart data string:', e);
            }
          }
        }
        
        console.log('Extracted cart data:', { product_id: productId, quantity });
        
        if (!productId) {
          console.error('Invalid cart data - missing product_id. Full data:', pendingAction.data);
          throw new Error('Invalid cart data: missing product_id');
        }
        
        console.log('Calling cartApi.addItem with:', { product_id: productId, quantity });
        response = await cartApi.addItem({
          product_id: productId,
          quantity: quantity,
        });
        console.log('Cart item added via cartApi - Response:', response?.data);
        console.log('Response status:', response?.status);
      } else {
        // For other actions, use apiClient
        switch (pendingAction.method.toUpperCase()) {
          case 'POST':
            response = await apiClient.post(url, pendingAction.data, {
              params: pendingAction.params,
            });
            break;
          case 'PUT':
            response = await apiClient.put(url, pendingAction.data, {
              params: pendingAction.params,
            });
            break;
          case 'PATCH':
            response = await apiClient.patch(url, pendingAction.data, {
              params: pendingAction.params,
            });
            break;
          case 'DELETE':
            response = await apiClient.delete(url, {
              params: pendingAction.params,
            });
            break;
          case 'GET':
          default:
            response = await apiClient.get(url, {
              params: pendingAction.params,
            });
            break;
        }
      }
      
      console.log('Pending action response:', response?.data);
    } catch (actionError: any) {
      console.error('Error executing pending action:', actionError);
      console.error('Error details:', {
        message: actionError?.message,
        response: actionError?.response?.data,
        status: actionError?.response?.status,
        url: url,
        data: pendingAction.data,
      });
      // Re-throw to be caught by outer catch
      throw actionError;
    }

    // Show success message and refresh relevant state based on the action
    if (url.includes('/cart') || url.includes('cart')) {
      if (pendingAction.method === 'POST') {
        // Verify the response indicates success
        if (response?.data || response?.status === 200 || response?.status === 201) {
          toast.success('Item added to cart!');
          
          // Refresh cart immediately and wait for it to complete
          try {
            const cartResult = await store.dispatch(fetchCart()).unwrap();
            console.log('Cart refreshed after pending action:', cartResult);
            
            // Double-check: wait a bit more and refresh again to ensure consistency
            await new Promise(resolve => setTimeout(resolve, 200));
            await store.dispatch(fetchCart());
          } catch (err) {
            console.error('Failed to refresh cart:', err);
            // Still return true as the add action succeeded
          }
        } else {
          throw new Error('Cart API did not return success response');
        }
      }
    } else if ((url.includes('/orders') || url.includes('orders')) && pendingAction.method === 'POST') {
      toast.success('Order placed successfully!');
      // Refresh cart to clear it after order
      try {
        await store.dispatch(fetchCart()).unwrap();
      } catch (err) {
        console.error('Failed to refresh cart:', err);
      }
    } else if ((url.includes('/wishlist') || url.includes('wishlist')) && pendingAction.method === 'POST') {
      toast.success('Added to wishlist!');
    }

    console.log('Pending action executed successfully:', response?.data);
    return true;
  } catch (error: any) {
    console.error('Failed to execute pending action:', error);
    console.error('Error details:', {
      message: error?.message,
      response: error?.response?.data,
      status: error?.response?.status,
    });
    // Show error toast so user knows something went wrong
    toast.error('Failed to complete your previous action. Please try again.');
    return false;
  }
};

/**
 * Clear any pending actions
 */
export const clearPendingAction = () => {
  sessionStorage.removeItem('pendingAction');
};

